import Button from "@/common/elements/Button";
import Checkbox from "@/common/elements/InputElements/CheckBox";
import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import { StyledFlex, StyledGridCol, StyledGridRow, StyledSectionHeading } from "@/common/styles";
import DynamicForm from "@/common/DynamicForm";
import {
  getOptionsFromRefList,
  hasCheckSecurity,
  isNonEmptyVariable,
  convertApiFields,
  getSQLTypeToHTMLType,
  normalizeUnknownSsn,
} from "@/util/common_function";
import { Form } from "antd";
import CommonDatePicker from "@/common/elements/DatePicker";
import dayjs from "dayjs";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Entity_types, Security_Types } from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";
import { MODULES_LIST_WITH_ID, SUB_MODULES_LIST_WITH_ID } from "@/util/common_variable";
import { useSelector } from "react-redux";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import { FieldSecurityProvider } from "@/contexts/FieldSecurityContext";
import SecureFormItem from "@/common/SecureFormItem";
import API_ENDPOINTS from "@/util/api";
import api from "@/services/axios_custom";
import { fetchExtraTableRecord } from "@/util/extraColumnActions";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Reliably coerce "True" / 1 / true → boolean for checkbox. */
const toBoolean = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    return ["true", "1", "y", "yes"].includes(value.trim().toLowerCase());
  }
  return false;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const AliasAddForm = forwardRef(
  ({ formData, isQuickAdd = false, quickSave, bookingsFieldsEnabled, startingPoint = "", tableName="BioAliases" }, ref) => {
    const [form] = Form.useForm();
    const [bioAlias, setBioAlias] = useState({ dob: null });
    const [saveBtnVisible, setSaveBtnVisible] = useState(false);
    const fullSearch = useSelector((state) => state.fullSearch);

    // -----------------------------------------------------------------------
    // BioAliasesExtra — PCI's per-client dynamic "_U" columns for one alias
    // (BioAliasesHelper.asp's "Additional Alias Information" section, e.g.
    // the "Adult" checkbox). Only rendered for the real Bookings BioAliases
    // usage of this form — Detentions reuses this same component
    // (tableName="DetentionsBioAliases") but legacy passes an empty
    // fieldSourceExtra there, so PCI itself never shows this section for
    // Detentions aliases either.
    // -----------------------------------------------------------------------
    const [bioAliasesExtraFields, setBioAliasesExtraFields] = useState([]);
    const [bioAliasesExtraFieldsReady, setBioAliasesExtraFieldsReady] = useState(false);
    const bioAliasesExtraTypeMapRef = useRef({}); // columnName -> htmlType
    const bioAliasesExtraIdRef = useRef(0); // BioAliasesExtraID, read by the parent's save (see ref API below)

    useEffect(() => {
      if (tableName !== "BioAliases") return;
      api
        .get(`${API_ENDPOINTS.getTableColumns}/BioAliasesExtra`)
        .then((response) => {
          const tableData = (response?.data || []).filter((d) => d?.columnName?.endsWith("_U"));
          const converted = convertApiFields(tableData, "BioAliasesExtra");
          const typeMap = {};
          converted.forEach((c) => { typeMap[c.columnName] = getSQLTypeToHTMLType(c?.type); });
          bioAliasesExtraTypeMapRef.current = typeMap;
          setBioAliasesExtraFields(converted);
          setBioAliasesExtraFieldsReady(true);
        })
        .catch((error) => {
          console.error("[AliasAddForm] BioAliasesExtra field defs fetch failed:", error);
        });
    }, [tableName]);

    // Fetch the saved extra row once a real bioAliasId is present (edit
    // mode) — a brand new alias has no id yet, so there's nothing to fetch
    // (and the save-side gate in AliasesList.jsx's saveClick skips it too).
    useEffect(() => {
      if (tableName !== "BioAliases" || !bioAliasesExtraFieldsReady) return;
      const bioAliasId = formData?.bioAliasId;
      if (!isNonEmptyVariable(bioAliasId)) {
        bioAliasesExtraIdRef.current = 0;
        return;
      }

      fetchExtraTableRecord("BioAliasesExtra", "bioAliasID", bioAliasId)
        .then((result) => {
          if (!result) return;
          bioAliasesExtraIdRef.current = result.raw?.BioAliasesExtraID ?? 0;
          const withDates = Object.keys(result.values).reduce((acc, key) => {
            const val = result.values[key];
            const htmlType = bioAliasesExtraTypeMapRef.current[key];
            const isDateField = htmlType === "date" || htmlType === "datetime";
            acc[key] = isDateField && isNonEmptyVariable(val) ? dayjs(val) : val;
            return acc;
          }, {});
          setBioAlias((prev) => ({ ...prev, ...withDates }));
        })
        .catch((error) => {
          console.error("[AliasAddForm] BioAliasesExtra fetch failed:", error);
        });
    }, [formData, bioAliasesExtraFieldsReady, tableName]);

    // -----------------------------------------------------------------------
    // Sync external formData → local state
    // -----------------------------------------------------------------------
    useEffect(() => {
      setBioAlias({
        ...formData,
        dob: formData?.dob ? dayjs(formData.dob) : null,
        questionedIdentity: toBoolean(formData?.questionedIdentity),
      });

      setSaveBtnVisible(
        isNonEmptyVariable(formData?.lName) ||
          isNonEmptyVariable(formData?.fName) ||
          isNonEmptyVariable(formData?.mName)
      );
    }, [formData]);

    // Sync local state → antd form
    useEffect(() => {
      form.setFieldsValue(bioAlias);
    }, [bioAlias, form]);

    // -----------------------------------------------------------------------
    // Expose form API to parent via ref
    // -----------------------------------------------------------------------
    useImperativeHandle(ref, () => ({
      submitForm: () => form.submit(),
      resetForm: () => form.resetFields(),
      getFormValues: () => form.getFieldsValue(),
      getFieldsError: () => form.getFieldsError(),
      isFieldsValidating: () => form.isFieldsValidating(),
      validateFields: () => form.validateFields(),
      // BioAliasesExtra field keys/type map/current PK — read by
      // AliasesList.jsx's saveClick to split the extra fields out of the
      // validated values and save them separately (see saveExtraTableRecord).
      bioAliasesExtraFieldKeys: bioAliasesExtraFields.map((f) => f.columnName),
      bioAliasesExtraTypeMap: bioAliasesExtraTypeMapRef.current,
      bioAliasesExtraID: bioAliasesExtraIdRef.current,
    }));

    // -----------------------------------------------------------------------
    // Track save-button visibility on field change
    // -----------------------------------------------------------------------
    const handleFieldsChange = () => {
      const { lName, fName, mName } = form.getFieldsValue();
      setSaveBtnVisible(
        isNonEmptyVariable(lName) ||
          isNonEmptyVariable(fName) ||
          isNonEmptyVariable(mName)
      );
    };

    // -----------------------------------------------------------------------
    // Q-Add button security check (sub-module level, not field level)
    // -----------------------------------------------------------------------
    const canQuickAdd = hasCheckSecurity(
      Security_Types.functionality,
      Entity_types.subModule,
      LABELS_CAPITAL.ADD,
      MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
      SUB_MODULES_LIST_WITH_ID.Arrest_Report
    );

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------
    return (
      // FieldSecurityProvider injects entity / startingPoint / bookingsFieldsEnabled
      // into every SecureFormItem below — no prop drilling needed.
      <FieldSecurityProvider
        entity={tableName}
        startingPoint={startingPoint}
        bookingsFieldsEnabled={bookingsFieldsEnabled}
      >
        <Form
          form={form}
          disabled={!bookingsFieldsEnabled}
          initialValues={bioAlias}
          onFieldsChange={handleFieldsChange}
        >
          <StyledGridRow gutter={16}>

            {/* Last Name */}
            <StyledGridCol>
              <SecureFormItem
                fieldName="lName"
                formItemProps={{
                  name: "lName",
                  label: "Last Name",
                  required: !fullSearch,
                  rules: !fullSearch
                    ? [{ required: true, message: "Last Name is required!" }]
                    : [],
                }}
              >
                <TextField />
              </SecureFormItem>
            </StyledGridCol>

            {/* First Name */}
            <StyledGridCol>
              <SecureFormItem
                fieldName="fName"
                formItemProps={{ name: "fName", label: "First Name" }}
              >
                <TextField />
              </SecureFormItem>
            </StyledGridCol>

            {/* Middle Name */}
            <StyledGridCol>
              <SecureFormItem
                fieldName="mName"
                formItemProps={{ name: "mName", label: "Middle Name" }}
              >
                <TextField />
              </SecureFormItem>
            </StyledGridCol>

            {/* Suffix */}
            <StyledGridCol lg={4}>
              <SecureFormItem
                fieldName="suffix"
                formItemProps={{ name: "suffix", label: "Suffix" }}
              >
                <Select
                  options={getOptionsFromRefList("Suffix")}
                  placeholder="Suffix"
                  showSearch
                  variant="outlined"
                />
              </SecureFormItem>
            </StyledGridCol>

            {/* Date of Birth */}
            <StyledGridCol>
              <SecureFormItem
                fieldName="dob"
                formItemProps={{ name: "dob", label: "DOB" }}
              >
                <CommonDatePicker
                  placeholder="MM/DD/YYYY"
                  format="MM/DD/YYYY"
                  showTime={false}
                  disableFutureDates
                  inputProps={{ maxLength: 10 }}
                />
              </SecureFormItem>
            </StyledGridCol>

            {/* SSN + Questioned Identity + Q-Add button */}
            <StyledGridCol lg={16}>
              <StyledFlex align="start" gap="14px">

                {/* SSN */}
                <SecureFormItem
                  fieldName="ssn"
                  formItemProps={{
                    name: "ssn",
                    label: "SSN",
                    rules: [
                      {
                        validator: (_, value) => {
                          if (!value) return Promise.resolve();
                          const ssnPattern = /^(?:\d{3}-\d{2}-\d{4}|\d{9}|unk.*)$/i;
                          if (!ssnPattern.test(value)) {
                            return Promise.reject(
                              "The value for SSN is not in the correct format. It should be a valid Social Security Number or 'UNKNOWN'. " +
                                "You can enter the Social Security Number with or without the dashes. " +
                                "For example, you could enter either '123456789' or '123-45-6789'."
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ],
                  }}
                >
                  <TextField
                    onBlur={(e) => {
                      const normalized = normalizeUnknownSsn(e?.target?.value);
                      if (normalized !== e?.target?.value) {
                        form.setFieldValue("ssn", normalized);
                      }
                    }}
                  />
                </SecureFormItem>

                <StyledFlex>
                  {/* Questioned Identity */}
                  <SecureFormItem
                    fieldName="questionedIdentity"
                    formItemProps={{
                      name: "questionedIdentity",
                      valuePropName: "checked",
                      label: (
                        <span onClick={(e) => e.preventDefault()}>
                          Questioned Identity
                        </span>
                      ),
                      layout: "horizontal",
                      labelPostion: "label-right",
                      paddingTop: "24px",
                      style: { minWidth: "200px" },
                    }}
                  >
                    <Checkbox />
                  </SecureFormItem>

                  {/* Q-Add button — uses sub-module security, not field security */}
                  <FormItem label="" paddingTop="24px">
                    {isQuickAdd && (
                      <Button
                        disabled={
                          !saveBtnVisible ||
                          !bookingsFieldsEnabled ||
                          !canQuickAdd
                        }
                        onClick={quickSave}
                      >
                        Q-Add
                      </Button>
                    )}
                  </FormItem>
                </StyledFlex>

              </StyledFlex>
            </StyledGridCol>

          </StyledGridRow>

          {/* Only in the full "Alias Information" modal — the condensed
              quick-add row above the list shouldn't repeat this section. */}
          {!isQuickAdd && tableName === "BioAliases" && bioAliasesExtraFields.length > 0 && (
            <FieldSecurityProvider
              entity="BioAliasesExtra"
              startingPoint={startingPoint}
              bookingsFieldsEnabled={bookingsFieldsEnabled}
            >
              <StyledSectionHeading>
                <span>Additional Alias Information</span>
              </StyledSectionHeading>
              <StyledGridRow gutter={16}>
                {bioAliasesExtraFields.map((sf, index) => (
                  <DynamicForm
                    key={index}
                    formAttributes={sf}
                    form={form}
                    bookingsFieldsEnabled={bookingsFieldsEnabled}
                  />
                ))}
              </StyledGridRow>
            </FieldSecurityProvider>
          )}
        </Form>
      </FieldSecurityProvider>
    );
  }
);

export default AliasAddForm;
